import EventDetails from "@/components/widgets/dashboard/calendar/eventModal/content/EventDetails";
import EventInvitation from "@/components/widgets/dashboard/calendar/eventModal/content/EventInvitation";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import type UserProfile from "@/domain/entities/user/UserProfile";
import { isEventPast } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

/** « 08:30 – 09:30 », or « Toute la journée ». */
export const eventTimeRange = (
    event: AgendaItem,
    language: string,
    allDay: string,
): string => {
    if (event.isAllDay) return allDay;
    const hour = (date: Date) =>
        new Date(date).toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });

    return `${hour(event.start)} – ${hour(event.end)}`;
};

/** An invitation still to come: not organised by the user, neither cancelled nor past. */
export const canAnswerInvitation = (
    event: AgendaItem,
    user: UserProfile | undefined,
) =>
    !!user &&
    !event.userIsOrganizer(user) &&
    !event.isCancelled &&
    !isEventPast(event);

interface EventDetailsBlockProps {
    event: AgendaItem;
    /** Accept or decline shown (canAnswerInvitation). */
    canAnswer: boolean;
    /** The event answered: the new state to keep. */
    onAnswered: (event: AgendaItem) => void;
    /** The details scroll past a height (in a line of the chat). */
    scrollDetails?: boolean;
}

/**
 * The details of an event (attendees, description, meeting link), accept or decline for an
 * invitation still to come, and the link to the calendar.
 */
const EventDetailsBlock = ({
    event,
    canAnswer,
    onAnswered,
    scrollDetails = false,
}: EventDetailsBlockProps) => {
    const { t } = useTranslation();
    const details = <EventDetails event={event} />;

    return (
        <>
            {scrollDetails ? (
                <div className="max-h-[320px] overflow-y-auto">{details}</div>
            ) : (
                details
            )}
            {canAnswer && (
                <EventInvitation
                    className="px-0"
                    compact
                    setUpdatedEvent={(updated) =>
                        onAnswered(updated as AgendaItem)
                    }
                    updatedEvent={event}
                />
            )}
            {!!event.webLink && (
                <a
                    className="flex w-fit items-center gap-1.5 text-[13px] font-semibold text-primary-dark hover:underline"
                    href={event.webLink}
                    rel="noreferrer"
                    target="_blank"
                >
                    <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                    {t("vistassistant.openInCalendar")}
                </a>
            )}
        </>
    );
};

export default EventDetailsBlock;
